import type { Fraction } from './types.ts';
import {
  resolveLetterMapping,
  resolveLetterOrBuiltInSample,
  type LetterMappingInput,
  type LetterMappingType,
  type LetterPolicy,
  type LetterSignerSource,
  type LetterVariant,
  type MappedLetterTemplate,
} from './letter-mappings.ts';

/**
 * APP-130: the letter coverage grid. A cell is one design: award type × signer × mix (D1). The grid,
 * the gap card on an award and the entry step's letter section all name cells the same way, and a
 * Ready cell is exactly what generation resolves (`resolveLetterMapping`), so they never disagree.
 */
export type LetterMix = Exclude<LetterVariant, 'any'>;
export const LETTER_MIXES: readonly LetterMix[] = Object.freeze([
  'cash_only',
  'options_only',
  'cash_and_options',
]);

export interface LetterCell {
  readonly awardType: LetterMappingType;
  readonly signerId: string;
  readonly mix: LetterMix;
}

/**
 * `SDB.<signerId>.cash_only`: the cell's key, and its deep link into Settings › Elections and
 * letters › Letters, `/settings/elections-letters/letters?cell=<key>` (the web's `letterCellHref`).
 */
export function letterCellKey(cell: LetterCell): string {
  return `${cell.awardType}.${cell.signerId}.${cell.mix}`;
}
const TYPES: readonly string[] = ['SDB', 'EDB', 'PAA', 'TAA', 'SPOT', 'SBIR', 'CRAD'];
export function parseLetterCellKey(key: string): LetterCell | null {
  const [awardType, signerId, mix, ...rest] = key.split('.');
  if (rest.length || !awardType || !signerId || !mix) return null;
  if (!TYPES.includes(awardType) || !(LETTER_MIXES as readonly string[]).includes(mix)) return null;
  if (!/^[0-9a-f-]{36}$/i.test(signerId)) return null;
  return { awardType: awardType as LetterMappingType, signerId, mix: mix as LetterMix };
}

/** A line's mix, as generation reads it (the server's `lineVariant`). */
export function letterMixOf(line: {
  readonly cashCents: number;
  readonly options: number;
}): LetterMix {
  return line.cashCents > 0
    ? line.options > 0
      ? 'cash_and_options'
      : 'cash_only'
    : 'options_only';
}

/** The settings in force that decide which mixes ordinary entry can produce. */
export interface LetterMixRules {
  /** `values.cashShare` for PAA: the cash part of a PAA's total value (before APP-151). */
  readonly paaCashShare: Fraction;
  /**
   * APP-151: `values.paaOptionShare` in force. When given, a PAA's amount is its cash: it always
   * pays cash, and pays options unless the share is nothing.
   */
  readonly paaOptionShare?: Fraction;
}
export interface LetterMixScope {
  readonly mix: LetterMix;
  /** Why the mix isn't shown, for the line under the grid; null when it's shown. */
  readonly hiddenBecause:
    'cash_only_type' | 'paa_always_cash' | 'paa_never_options' | 'paa_never_cash' | null;
}

/**
 * D3 (Tom: "Hide them automatically"): a mix is hidden when ordinary entry can't produce it under
 * the settings in force. TAA and Spot are cash only. Since APP-151 a PAA pays its amount in cash,
 * so Options only can't happen, and Cash only shows for a PAA too small for one option. Before, it paid `total × cashShare` in cash, so
 * while the share is above zero it always pays some cash (Options only can't happen; only an
 * Executive's cash-share change at approval reaches it), and at a share of one it pays no options.
 * Its Cash only shows: a small PAA's option part can be under one share price. Sector and Executive
 * bonuses take cash and options as entered, so all three show. SBIR and CRAD have no grid yet.
 */
export function letterMixScopes(
  awardType: LetterMappingType,
  rules: LetterMixRules,
): readonly LetterMixScope[] {
  const hidden = (mix: LetterMix): LetterMixScope['hiddenBecause'] => {
    if (awardType === 'TAA' || awardType === 'SPOT')
      return mix === 'cash_only' ? null : 'cash_only_type';
    if (awardType !== 'PAA') return null;
    if (rules.paaOptionShare !== undefined) {
      if (mix === 'options_only') return 'paa_always_cash';
      return rules.paaOptionShare.num === 0 && mix === 'cash_and_options'
        ? 'paa_never_options'
        : null;
    }
    const { num, den } = rules.paaCashShare;
    if (num === 0) return mix === 'options_only' ? null : 'paa_never_cash';
    if (num >= den) return mix === 'cash_only' ? null : 'paa_never_options';
    return mix === 'options_only' ? 'paa_always_cash' : null;
  };
  return LETTER_MIXES.map((mix) => ({ mix, hiddenBecause: hidden(mix) }));
}

/** APP-221: why a mix ordinary entry can produce still needs no letter of its own. */
export type LetterNotNeededBecause = 'paa_always_options';

/**
 * APP-221 (Tom: "since that one letter is not a real scenario, let's just update the UI to show it
 * as NA. And then that way we can show the setup as complete."): a fixed rule, like D3's. Since
 * APP-151 a PAA pays Cash only only when its options round down to none, which the client says
 * never happens, so its Cash only cell needs no letter while the option share is above nothing. At
 * a share of nothing every PAA is cash only and that's the one cell, so it's needed. The cell stays
 * in the grid, reads Not needed and leaves the counts; a letter of its own there still shows (a
 * real one reads Ready), and an award that ever does pay cash only still falls back as before.
 */
export function letterMixNotNeeded(
  awardType: LetterMappingType,
  rules: LetterMixRules,
  mix: LetterMix,
): LetterNotNeededBecause | null {
  return awardType === 'PAA' &&
    mix === 'cash_only' &&
    rules.paaOptionShare !== undefined &&
    rules.paaOptionShare.num > 0
    ? 'paa_always_options'
    : null;
}

export function possibleLetterMixes(
  awardType: LetterMappingType,
  rules: LetterMixRules,
): readonly LetterMix[] {
  return letterMixScopes(awardType, rules)
    .filter((s) => s.hiddenBecause === null)
    .map((s) => s.mix);
}

/** A library row as the grid needs it: the mapping facts plus where it is in review. */
export interface CoverageTemplate extends MappedLetterTemplate {
  /** The per-design number (D1/D2, QA-066); null until the row is numbered. */
  readonly designVersion: number | null;
  /** Sent back for a fix (D5); never offered for approval again. */
  readonly sentBack: boolean;
  readonly retired: boolean;
}
export type LetterCellState =
  | { readonly state: 'ready'; readonly templateId: string; readonly designVersion: number | null }
  | { readonly state: 'sample_to_review'; readonly templateId: string }
  /** Approved but not in use (an earlier version, or approved before it was mapped): offer Use it. */
  | { readonly state: 'approved'; readonly templateId: string }
  /**
   * APP-212: no real letter, so the built-in sample letter is made here (never Ready, so the gap
   * stays visible). Names the newest sent-back row, as Missing does, for a corrected upload.
   */
  | { readonly state: 'sample'; readonly sentBackId: string | null }
  | { readonly state: 'missing'; readonly sentBackId: string | null };

/** APP-212: whether the award type has the built-in sample letter to fall back to. */
export interface LetterCellOptions {
  readonly builtInSample?: boolean;
}

/**
 * A cell's versions newest first: the cell's own mix before a general-purpose one, as generation
 * and approve-and-map prefer it (the two are numbered in separate series, D1, so their numbers
 * don't compare), then the higher design number, then id, so the same library always reads alike.
 */
function cellOrder(mix: LetterMix): (a: CoverageTemplate, b: CoverageTemplate) => number {
  return (a, b) =>
    Number(b.variant === mix) - Number(a.variant === mix) ||
    (b.designVersion ?? 0) - (a.designVersion ?? 0) ||
    (a.id < b.id ? -1 : a.id > b.id ? 1 : 0);
}

/**
 * What one cell reads. Ready is `resolveLetterMapping` for this signer and mix, nothing else. If
 * there's no Ready letter, the newest draft waiting for review (not approved, not sent back, not
 * retired) makes it Sample to review; then the newest approved, usable, unmapped version makes it
 * Approved; otherwise it's Missing, naming the newest sent-back row so the cell can offer a
 * corrected upload. Newest is `cellOrder`: the cell's own mix first. APP-212: with the built-in
 * sample, a cell generation falls back to it in (`resolveLetterOrBuiltInSample`) reads Sample
 * where it would read Missing; Ready, Sample to review and Approved come first, as each has a
 * letter of its own and a step left on it.
 */
export function letterCellState(
  cell: LetterCell,
  mapping: Omit<
    LetterMappingInput,
    'awardType' | 'variant' | 'templates' | 'overrideId' | 'overrideReason'
  >,
  templates: readonly CoverageTemplate[],
  options: LetterCellOptions = {},
): LetterCellState {
  const resolved = resolveLetterMapping({
    ...mapping,
    awardType: cell.awardType,
    variant: cell.mix,
    templates,
  });
  if (resolved.ok && resolved.value.signerId === cell.signerId) {
    const t = templates.find((t) => t.id === resolved.value.templateId);
    return {
      state: 'ready',
      templateId: resolved.value.templateId,
      designVersion: t?.designVersion ?? null,
    };
  }
  const own = templates
    .filter(
      (t) =>
        t.awardType === cell.awardType &&
        t.signerId === cell.signerId &&
        (t.variant === cell.mix || t.variant === 'any') &&
        !t.retired,
    )
    .sort(cellOrder(cell.mix));
  const waiting = own.find((t) => !t.approved && !t.sentBack);
  if (waiting) return { state: 'sample_to_review', templateId: waiting.id };
  const approved = own.find((t) => t.approved && t.usable);
  if (approved) return { state: 'approved', templateId: approved.id };
  const sentBackId = own.find((t) => t.sentBack)?.id ?? null;
  if (options.builtInSample === true) {
    const fallback = resolveLetterOrBuiltInSample({
      ...mapping,
      awardType: cell.awardType,
      variant: cell.mix,
      templates,
      builtInSample: true,
    });
    if (
      fallback.ok &&
      fallback.value.kind === 'built_in_sample' &&
      fallback.value.signerId === cell.signerId
    )
      return { state: 'sample', sentBackId };
  }
  return { state: 'missing', sentBackId };
}

/**
 * The newest draft waiting for review behind a cell's Ready or Approved version (not approved, not
 * sent back, not retired), by `cellOrder`, and only one that would take that version's place: its
 * own mix where the version is general-purpose, or the same specificity and a higher design. An
 * older or general-purpose draft behind a letter made for the mix isn't waiting there, so a demo
 * letter left in review never reads as about to replace a client's letter.
 */
function waitingDraft(
  cell: LetterCell,
  templates: readonly CoverageTemplate[],
  currentId: string,
): CoverageTemplate | undefined {
  const current = templates.find((t) => t.id === currentId);
  return templates
    .filter(
      (t) =>
        t.awardType === cell.awardType &&
        t.signerId === cell.signerId &&
        (t.variant === cell.mix || t.variant === 'any') &&
        !t.retired &&
        !t.approved &&
        !t.sentBack &&
        (current === undefined || replaces(cell.mix, t, current)),
    )
    .sort(cellOrder(cell.mix))[0];
}

/** Whether `draft` would read ahead of `current` in this mix's cell: more specific, or newer. */
function replaces(mix: LetterMix, draft: CoverageTemplate, current: CoverageTemplate): boolean {
  const specific = Number(draft.variant === mix) - Number(current.variant === mix);
  if (specific !== 0) return specific > 0;
  return (draft.designVersion ?? 0) > (current.designVersion ?? 0);
}

/**
 * Why a row's signer can't be resolved: the same codes `resolveLetterMapping` fails with for an
 * award in that row (no policy for the type; no signer, such as a sector without a Sector Lead or
 * no CEO set; or two signers).
 */
export type LetterRowProblem =
  'letter_policy_missing' | 'letter_signer_missing' | 'letter_signer_ambiguous';

/** The directory and library facts the grid is drawn from, as generation reads them. */
export interface LetterCoverageInput {
  readonly awardType: LetterMappingType;
  readonly rules: LetterMixRules;
  readonly policies: readonly LetterPolicy[];
  /** The CEO / President, when set and active (generation reads it so). */
  readonly ceoId: string | null;
  /** Sectors that can give the award, each with its active Sector Leads. */
  readonly sectors: readonly { readonly id: string; readonly leadIds: readonly string[] }[];
  /** Active people on the Executives list: the givers an Executive bonus can have. */
  readonly executiveIds: readonly string[];
  readonly templates: readonly CoverageTemplate[];
  /** APP-212: the type has the built-in sample letter, so a cell with no letter reads Sample. */
  readonly builtInSample?: boolean;
}
/**
 * A grid cell's state: the cell's own (`letterCellState`), or APP-221's Not needed where the cell
 * has no letter of its own and `letterMixNotNeeded` says it needs none. Only the grid reads Not
 * needed: generation and the entry form keep `letterCellState`, so an award there still reads Sample.
 */
export type LetterCoverageCellState =
  LetterCellState | { readonly state: 'not_needed'; readonly because: LetterNotNeededBecause };

export interface LetterCoverageCell {
  readonly cell: LetterCell;
  /** `letterCellKey(cell)`: the `?cell=` deep link. */
  readonly key: string;
  readonly state: LetterCoverageCellState;
  /** A newer draft waiting for review while the cell is Ready or Approved (D6: it would replace). */
  readonly waitingId: string | null;
}
export interface LetterCoverageRow {
  /** Who signs this row's letters; null on a problem row. */
  readonly signerId: string | null;
  /** The policy the row comes from; null when the type has no single policy. */
  readonly source: LetterSignerSource | null;
  /** The awarding sector, for a Sector Lead row. */
  readonly sectorId: string | null;
  readonly problem: LetterRowProblem | null;
  /** What generation resolves an award in this row with (giver, sector leads, CEO, policies). */
  readonly mapping: Omit<
    LetterMappingInput,
    'awardType' | 'variant' | 'templates' | 'overrideId' | 'overrideReason'
  >;
  /** One per mix shown; none on a problem row. */
  readonly cells: readonly LetterCoverageCell[];
}
export interface LetterCoverageCounts {
  readonly ready: number;
  /** Drafts waiting for review: Sample to review cells, and Ready or Approved cells with one. */
  readonly toReview: number;
  /** Approved but not in use (Use it). */
  readonly approved: number;
  /** APP-212: cells where the built-in sample letter is made, for want of a real one. */
  readonly sample: number;
  /** Cells with no letter, and a problem row's cells: letters there can't be made today. */
  readonly missing: number;
  /** APP-221: cells shown that need no letter (`letterMixNotNeeded`), a problem row's included. */
  readonly notNeeded: number;
  /**
   * Every shown cell that needs a letter, a problem row's included: what Ready counts against.
   * Hidden mixes never count (D3), and Not needed cells are counted apart (APP-221).
   */
  readonly total: number;
}
export interface LetterCoverageGrid {
  readonly awardType: LetterMappingType;
  /** The columns: the mixes ordinary entry can produce under the settings in force (D3). */
  readonly mixes: readonly LetterMix[];
  /** The mixes that aren't columns, and why: the line under the grid. */
  readonly hidden: readonly (LetterMixScope & {
    readonly hiddenBecause: NonNullable<LetterMixScope['hiddenBecause']>;
  })[];
  /** APP-221: the columns that need no letter, and why: a line under the grid too. */
  readonly notNeeded: readonly {
    readonly mix: LetterMix;
    readonly because: LetterNotNeededBecause;
  }[];
  readonly rows: readonly LetterCoverageRow[];
  readonly counts: LetterCoverageCounts;
}

/**
 * APP-130: one award type's coverage grid. Rows are the signers the type's policy can resolve to,
 * each with the facts generation would use for an award there: a Sector Lead policy gives a row per
 * sector (its leads), an Executive policy a row per Executive (the giver), a CEO policy one row
 * (the CEO / President), a named-person policy that person. A row whose signer can't be resolved
 * is a problem row, with the code generation fails with. Columns are `letterMixScopes`; each cell
 * is `letterCellState`, so a Ready cell is exactly what generation resolves.
 */
export function letterCoverage(input: LetterCoverageInput): LetterCoverageGrid {
  const scopes = letterMixScopes(input.awardType, input.rules);
  const mixes = scopes.filter((s) => s.hiddenBecause === null).map((s) => s.mix);
  const hidden = scopes.flatMap((s) =>
    s.hiddenBecause === null ? [] : [{ mix: s.mix, hiddenBecause: s.hiddenBecause }],
  );
  const notNeeded = mixes.flatMap((mix) => {
    const because = letterMixNotNeeded(input.awardType, input.rules, mix);
    return because === null ? [] : [{ mix, because }];
  });
  const policies = input.policies.filter((p) => p.awardType === input.awardType);
  const base = { giverId: null, sectorLeadIds: [], ceoId: input.ceoId, policies: input.policies };
  type Draft = Omit<LetterCoverageRow, 'cells' | 'problem'>;
  const drafts: Draft[] = [];
  const policy = policies.length === 1 ? policies[0] : undefined;
  if (!policy) drafts.push({ signerId: null, source: null, sectorId: null, mapping: base });
  else if (policy.source === 'sector_lead')
    for (const sector of input.sectors) {
      const leads = [...new Set(sector.leadIds)];
      drafts.push({
        signerId: leads.length === 1 ? (leads[0] ?? null) : null,
        source: policy.source,
        sectorId: sector.id,
        mapping: { ...base, sectorLeadIds: leads },
      });
    }
  else if (policy.source === 'executive') {
    const givers = [...new Set(input.executiveIds)];
    if (givers.length === 0)
      drafts.push({ signerId: null, source: policy.source, sectorId: null, mapping: base });
    for (const giverId of givers)
      drafts.push({
        signerId: giverId,
        source: policy.source,
        sectorId: null,
        mapping: { ...base, giverId },
      });
  } else
    drafts.push({
      signerId: policy.source === 'ceo' ? input.ceoId : policy.personId,
      source: policy.source,
      sectorId: null,
      mapping: base,
    });

  const rows = drafts.map((row): LetterCoverageRow => {
    // The row's problem is what generation says for an award in it, never a rule of its own.
    const probe = resolveLetterMapping({
      ...row.mapping,
      awardType: input.awardType,
      variant: mixes[0] ?? 'cash_only',
      templates: [],
    });
    const failed = probe.ok ? null : (probe.issues[0]?.code ?? null);
    const problem =
      failed === 'letter_policy_missing' ||
      failed === 'letter_signer_missing' ||
      failed === 'letter_signer_ambiguous'
        ? failed
        : null;
    const signerId = problem === null ? row.signerId : null;
    if (signerId === null)
      return { ...row, signerId: null, problem: problem ?? 'letter_signer_missing', cells: [] };
    return {
      ...row,
      signerId,
      problem: null,
      cells: mixes.map((mix) => {
        const cell: LetterCell = { awardType: input.awardType, signerId, mix };
        const own = letterCellState(cell, row.mapping, input.templates, {
          builtInSample: input.builtInSample === true,
        });
        // APP-221: a letter of its own (Ready, Sample to review, Approved) shows as ever.
        const because = notNeeded.find((n) => n.mix === mix)?.because;
        const state: LetterCoverageCellState =
          because !== undefined && (own.state === 'sample' || own.state === 'missing')
            ? { state: 'not_needed', because }
            : own;
        const waiting =
          state.state === 'ready' || state.state === 'approved'
            ? waitingDraft(cell, input.templates, state.templateId)
            : undefined;
        return { cell, key: letterCellKey(cell), state, waitingId: waiting?.id ?? null };
      }),
    };
  });
  const cells = rows.flatMap((r) => r.cells);
  const problemRows = rows.filter((r) => r.problem !== null).length;
  // A problem row's cells count as Missing, but a column that needs no letter stays Not needed.
  const problemCells = problemRows * (mixes.length - notNeeded.length);
  const notNeededCells =
    cells.filter((c) => c.state.state === 'not_needed').length + problemRows * notNeeded.length;
  return {
    awardType: input.awardType,
    mixes,
    hidden,
    notNeeded,
    rows,
    counts: {
      ready: cells.filter((c) => c.state.state === 'ready').length,
      toReview: cells.filter((c) => c.state.state === 'sample_to_review' || c.waitingId !== null)
        .length,
      approved: cells.filter((c) => c.state.state === 'approved').length,
      sample: cells.filter((c) => c.state.state === 'sample').length,
      missing: cells.filter((c) => c.state.state === 'missing').length + problemCells,
      notNeeded: notNeededCells,
      total: cells.length + problemRows * mixes.length - notNeededCells,
    },
  };
}

/** Several grids' counts, added up (the count pills and the go-live step). */
export function letterCoverageTotals(
  grids: readonly Pick<LetterCoverageGrid, 'counts'>[],
): LetterCoverageCounts {
  const sum = (k: keyof LetterCoverageCounts) => grids.reduce((n, g) => n + g.counts[k], 0);
  return {
    ready: sum('ready'),
    toReview: sum('toReview'),
    approved: sum('approved'),
    sample: sum('sample'),
    missing: sum('missing'),
    notNeeded: sum('notNeeded'),
    total: sum('total'),
  };
}
